import { FC } from 'react';
import { useRenderTool } from '@copilotkit/react-core/v2';
import ChatGenerativeUIBlock, { ChatGenerativeUIBlockStatus } from '../ChatGenerativeUIBlock';

// Local structural mirror of the v2 wildcard render-prop. The same dual-
// installation pattern documented in `runtime/copilotKitRuntime.ts` applies
// here: importing the SDK type triggers nominal mismatches when two copies
// of `@copilotkit/*` resolve through different package paths. The runtime
// shape — confirmed at
// `node_modules/@copilotkit/react-core/dist/copilotkit-DFaI4j2r.d.mts:1342–1369`
// (ReactToolCallRenderer.render) and assembled by `useRenderTool` at
// `node_modules/@copilotkit/react-core/dist/copilotkit-OmIUrWym.mjs:3878–3905`
// (which spreads `parameters: props.args` on top of the renderer props) — is
// captured locally so the registration is resilient to either copy winning.
interface WildcardRenderProps {
  name: string;
  toolCallId: string;
  args?: unknown;
  parameters?: unknown;
  status: 'inProgress' | 'executing' | 'complete' | string;
  result?: string;
}

/**
 * Adapter from the v2 `useRenderTool` wildcard render-prop union into the
 * presentational `ChatGenerativeUIBlock`. The hook spreads `parameters` on
 * top of the underlying `ReactToolCallRenderer` props at registration time,
 * so we read `parameters` first and fall back to `args` for forward-compat.
 * @param props - v2 wildcard render props.
 * @returns the inline rich block, or null if the props are unrenderable.
 */
const WildcardBlockRenderer: FC<WildcardRenderProps> = (props) => {
  const status = (props.status ?? 'inProgress') as ChatGenerativeUIBlockStatus;
  const parameters = props.parameters !== undefined ? props.parameters : props.args;
  return (
    <ChatGenerativeUIBlock
      name={props.name}
      toolCallId={props.toolCallId}
      parameters={parameters}
      status={status}
      result={typeof props.result === 'string' ? props.result : undefined}
    />
  );
};

/**
 * Registers `ChatGenerativeUIBlock` as the wildcard (`name: "*"`) tool-call
 * renderer with CopilotKit v2's frontend-tool registry.
 *
 * **Hook target.** The SDK's actual registration hook is `useRenderTool` (see
 * `node_modules/@copilotkit/react-core/dist/copilotkit-DFaI4j2r.d.mts:1605`).
 * The Phase 4a ADR and the v4 plan both refer to the registration hook as
 * `useRenderToolCall`, but `useRenderToolCall` is in fact the *consumer*
 * helper that returns `({ toolCall, toolMessage }) => ReactElement | null`
 * (see line 1313). This file follows the SDK's actual API; the deviation is
 * documented in Flow 4's report to engineering.
 *
 * **Catch-all ordering.** The wildcard renderer is used as a fallback when
 * no exact name-matched renderer is registered (see
 * `node_modules/@copilotkit/react-core/dist/copilotkit-OmIUrWym.mjs:3468`).
 * Flow 1's tool-specific renderers will preempt this catch-all by name.
 */
export const useRegisterCatchAllToolRender = (): void => {
  useRenderTool(
    {
      name: '*',
      render: (renderProps) => <WildcardBlockRenderer {...(renderProps as WildcardRenderProps)} />
    },
    []
  );
};

/**
 * Mountable wrapper component that registers the catch-all tool renderer and
 * renders nothing. Mounted inside `<CopilotKitProvider>` so the registration
 * hook resolves the live core via `useCopilotKit()`.
 *
 * Lives in `client-chat/actions/` (the Phase 0 lint rule pre-allows
 * `@copilotkit/*` and `@ag-ui/*` imports here; see ADR-0001).
 */
export const CatchAllToolRenderRegistry: FC = () => {
  useRegisterCatchAllToolRender();
  return null;
};

export default CatchAllToolRenderRegistry;
